import { Icon } from '@assembly-js/design-system';
import clsx from 'clsx';
import React, { useContext } from 'react';

import { WorkspaceAvatar } from 'src/components/WorkspaceAvatar/WorkspaceAvatar';
import { SidebarThemeContext } from 'src/context/sidebarThemeContext';

interface SidebarSwitcherButtonProps {
  iconSrc?: string;
  iconFallbackColor?: string;
  iconSize?: number;
  name: string;
  isOpen: boolean;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Optional avatar element rendered in place of the default WorkspaceAvatar.
   * Lets a caller supply a company-branded avatar without this shared button
   * needing to know about clients; defaults to WorkspaceAvatar when omitted.
   */
  iconSlot?: React.ReactNode;
}

export const SidebarSwitcherButton = React.forwardRef<
  HTMLButtonElement,
  SidebarSwitcherButtonProps
>(
  (
    {
      iconSrc,
      iconFallbackColor,
      iconSize = 25,
      name,
      isOpen,
      onClick,
      iconSlot,
    },
    ref,
  ) => {
    const { sidebarTextColor, sidebarItemHoverBackground } =
      useContext(SidebarThemeContext);

    return (
      <button
        ref={ref}
        type="button"
        aria-expanded={isOpen}
        onClick={onClick}
        style={
          {
            '--sidebar-item-hover-bg': sidebarItemHoverBackground,
          } as React.CSSProperties
        }
        className={clsx(
          'w-full h-9 flex items-center justify-start px-2 py-1 rounded border-none cursor-pointer',
          'hover:bg-[var(--sidebar-item-hover-bg)] active:bg-[var(--sidebar-item-hover-bg)]',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-info-text',
          isOpen ? 'bg-[var(--sidebar-item-hover-bg)]' : 'bg-transparent',
        )}
      >
        {iconSlot ?? (
          <WorkspaceAvatar
            iconSrc={iconSrc ?? null}
            workspaceName={name}
            size={iconSize}
            containerClassName="mr-2"
            fallbackBackgroundColor={iconFallbackColor}
          />
        )}
        <span
          className="truncate text-[13px] font-medium"
          style={{ color: sidebarTextColor }}
        >
          {name}
        </span>
        <Icon
          icon="ChevronDown"
          className="ml-[6px] w-3 h-3 shrink-0"
          style={{ color: sidebarTextColor }}
        />
      </button>
    );
  },
);

SidebarSwitcherButton.displayName = 'SidebarSwitcherButton';
